import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import {
	SCREENER_COLUMNS,
	type ScreenerColumn,
	type ScreenerFilterData,
} from "epsm-api-types/models";
import type { MultiValue } from "react-select";
import { Button } from "@/components/ui/button";
import {
	type FilterDataContextProps,
	useFilterData,
} from "@/context/FilterDataContext";
import {
	useWidgetView,
	type ViewState,
	type WidgetViewContextProps,
} from "@/context/WidgetViewContext";
import type { Option } from "@/interfaces/Option";
import { Dropdown } from "./ui/Dropdown";

export const ListFilters = ({
	height,
	refetch,
}: {
	height: number;
	refetch: UseInfiniteQueryResult["refetch"];
}) => {
	const { selectedColumn } = useWidgetView() as {
		selectedColumn: ViewState<ScreenerColumn>;
	} & WidgetViewContextProps;
	const { filterData, updateResults, listOptions, setListOptions, getOptions } =
		useFilterData() as {
			filterData: ScreenerFilterData;
		} & FilterDataContextProps;

	let options: Option[];

	if (selectedColumn.Screener.type === "bool") {
		options = [true, false].map((item) =>
			getOptions(item, selectedColumn.Screener),
		);
	} else if (
		selectedColumn.Screener?.options &&
		selectedColumn.Screener.type === "str"
	) {
		options = Object.entries(selectedColumn.Screener.options).map(
			([key, label]) => ({ value: key, label }),
		);
	} else {
		options = filterData.items
			.filter((item) => item != null)
			.map((item) => getOptions(item, selectedColumn.Screener));
	}

	const column = SCREENER_COLUMNS.find(
		(column) => column.name === selectedColumn.Screener.name,
	);

	const columnAbbr =
		column?.titleAbbreviation ?? column?.titleShort ?? column?.title;
	const columnTitle =
		column?.title === columnAbbr
			? column?.title
			: `${column?.title} (${columnAbbr})`;

	const handleUpdateResults = () => {
		const newResult: Array<{
			name: string;
			condition: "in";
			value: unknown[];
		}> = [];

		const value = listOptions.map((option) => option.value);

		newResult.push({
			name: selectedColumn.Screener.name,
			condition: "in",
			value,
		});

		updateResults(newResult, refetch);
	};

	return (
		<div className="flex flex-col items-center max-w-[360px] w-full">
			<div className="flex flex-col w-full gap-2">
				<h3 className="text-[var(--light-gray)]">{columnTitle}</h3>
				<div className="mt-5 mb-3">
					<Dropdown
						options={options}
						maxMenuHeight={height - 84}
						placeholder="Choose option"
						value={listOptions}
						onChange={(newValue) =>
							setListOptions(newValue as MultiValue<Option>)
						}
						isTypeList={true}
					/>
				</div>
			</div>

			<Button
				className="self-end h-[18px]"
				variant="outline"
				disabled={!listOptions.length}
				onClick={handleUpdateResults}
			>
				Add
			</Button>
		</div>
	);
};
